import React, { Suspense, useContext, useState, useRef } from "react";
import { useAuth } from "react-oidc-context";
import Home from "./Home";
import HomeOnline from "./HomeOnline";
import Dashboard from "./Dashboard";
import Loader from "./Loader";
import { UserPreferenceService } from "../../service/UserPreferenceService";
import { setStringToStorage, setObjectToStorage } from "../../utils/storagehelper";
import { UserSettings, WorkbookSettings } from "../../types/app.storage.enums";
import { initializeSheetOptions, initializeSheetState } from "../../utils/sheetoptions";
import { AppContext } from "./context/AppContext";
import { getStringFromWorkbookStorage, setStringToWorkbookStorage } from "../../utils/officestoragehelper";

const DIALOG_FLAG_KEY = "xl_IsOfficeDialog";
const LOGIN_INTERACTION_ERRORS = ["login_required", "interaction_required", "consent_required"];

const isLoginInteractionError = (error) => {
  if (!error) {
    return false;
  }
  const text = `${error.message || ""} ${error.error || ""} ${error.name || ""}`.toLowerCase();
  return LOGIN_INTERACTION_ERRORS.some((code) => text.includes(code));
};

const isOfficeDialogWindow = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("Popup") === "true") {
      sessionStorage.setItem(DIALOG_FLAG_KEY, "true");
      return true;
    }
    if (params.get("Popup") === "false") {
      sessionStorage.removeItem(DIALOG_FLAG_KEY);
      return false;
    }
    return sessionStorage.getItem(DIALOG_FLAG_KEY) === "true";
  } catch {
    return false;
  }
};

const getOfficePlatform = () => {
  try {
    return Office?.context?.diagnostics?.platform;
  } catch {
    return undefined;
  }
};

const App = () => {
  const auth = useAuth();
  const context = useContext(AppContext);
  const userPreferenceService = new UserPreferenceService();
  const [platformType, setplatformType] = useState(getOfficePlatform());
  const [popstate, setpopstate] = useState(isOfficeDialogWindow());
  const officeOnline = platformType === Office.PlatformType.OfficeOnline;
  const triedInteractiveLogin = useRef(false);

  const saveAttributeConfig = () => {
    userPreferenceService.getConfig().then((result) => {
      if (result) setObjectToStorage(UserSettings.LstGetFunctionAttributeName, result);
    });
  };

  const initializeErrorHandlingType = () => {
    getStringFromWorkbookStorage(WorkbookSettings.ErrorHandlingType).then((handlingType) => {
      if (handlingType === "") setStringToWorkbookStorage(WorkbookSettings.ErrorHandlingType, true);
    });
  };

  const saveBatchCountConfig = () => {
    userPreferenceService.getBatchConfig().then((result) => {
      if (result) {
        if (result.investmentBatchCount)
          setStringToStorage(UserSettings.InvestmentBatchCount, result.investmentBatchCount);
        if (result.portfolioBatchCount)
          setStringToStorage(UserSettings.PortfolioBatchCount, result.portfolioBatchCount);
        if (result.investorBatchCount) setStringToStorage(UserSettings.InvestorBatchCount, result.investorBatchCount);
        if (result.refreshTokenTimer) setStringToStorage(UserSettings.RefreshTokenTimer, result.refreshTokenTimer);
        if (result.uaBatchCount) setStringToStorage(UserSettings.UABatchCount, result.uaBatchCount);
        if (result.cfarInvestmentBatchCount)
          setStringToStorage(UserSettings.CfarInvestmentBatchCount, result.cfarInvestmentBatchCount);
        if (result.cfarPortfolioBatchCount)
          setStringToStorage(UserSettings.CfarPortfolioBatchCount, result.cfarPortfolioBatchCount);
      }
    });
  };

  const handleSheetAdded = async (_e) => {
    await initializeSheetOptions();
    initializeSheetState();
    context.setIsSheetAddedOrRemoved((pre) => !pre);
  };

  React.useEffect(() => {
    return auth.events.addAccessTokenExpiring(() => {
      auth.signinSilent();
    });
  }, [auth.events, auth.signinSilent]);

  React.useEffect(() => {
    if (!popstate || auth.isLoading || auth.isAuthenticated || !auth.error || triedInteractiveLogin.current) {
      return;
    }
    if (!isLoginInteractionError(auth.error)) {
      return;
    }
    triedInteractiveLogin.current = true;
    void auth.signinRedirect();
  }, [popstate, auth.isLoading, auth.isAuthenticated, auth.error, auth.signinRedirect]);

  React.useEffect(() => {
    if (auth.isAuthenticated) {
      saveAttributeConfig();
      saveBatchCountConfig();
    }
  }, [auth.isAuthenticated]);

  React.useEffect(() => {
    const inDialog = isOfficeDialogWindow();
    setpopstate(inDialog);
    setplatformType(getOfficePlatform());

    if (inDialog) {
      return;
    }

    Excel.run(async (context) => {
      const worksheets = context.workbook.worksheets;
      worksheets.onAdded.add(handleSheetAdded);
      await context.sync();
      return () => {
        worksheets.onAdded.remove(handleSheetAdded);
      };
    }).catch((error) => {
      console.log("Error: " + error);
    });
    initializeSheetOptions();
    initializeSheetState();
    initializeErrorHandlingType();
  }, []);

  if (auth.isLoading) {
    return <Loader></Loader>;
  }

  if (auth.error) {
    if (popstate && isLoginInteractionError(auth.error)) {
      return <Loader></Loader>;
    }
    return <div>Oops... {auth.error.message}</div>;
  }

  return (
    <>
      {auth.isAuthenticated ? (
        <Suspense fallback={<Loader></Loader>}>
          <Dashboard></Dashboard>
        </Suspense>
      ) : officeOnline && !popstate ? (
        <div>
          <HomeOnline></HomeOnline>
        </div>
      ) : (
        <div>
          <Home></Home>
        </div>
      )}
    </>
  );
};

export default App;
